/**
 * enumLabels.ts — Centralized enum/status localization (Arabic remediation T4.7–T4.12).
 *
 * Many route files interpolate raw data enum/status tokens directly into JSX
 * (e.g. `{matter.lifecycle}`, `{task.priority}`, `{inv.status}`), which leaks
 * English even when the UI is in Arabic mode. This module provides ONE helper,
 * `enumLabel(value, lang)`, that maps a token to a localized, human-readable
 * label, with a safe humanizing fallback for unmapped tokens.
 *
 * Design:
 * - Tokens are normalized (trim, uppercase, non-alphanumerics → "_") so that
 *   "in_progress", "In Progress", "IN-PROGRESS" all resolve to the same entry.
 * - The map is flat and keyed by normalized token. Where the same token has a
 *   single sensible label across contexts (PENDING, CONNECTED, HIGH, …) one
 *   entry serves all categories.
 * - Composite priority tokens ("P1 URGENT") are mapped explicitly.
 * - English fallback humanizes the raw token (UPPER_SNAKE → Title Case);
 *   already-presentable strings (no underscores) are returned as-is.
 * - Arabic fallback returns the English humanized form for unmapped tokens so
 *   nothing renders blank — unmapped Arabic tokens are a coverage gap to close,
 *   not a crash.
 */

import type { LanguageType } from "./mockState";

interface Label {
  en: string;
  ar: string;
}

/** Normalize any raw enum/status token to a stable lookup key. */
export function normalizeEnumKey(value: string): string {
  return value
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/**
 * Flat normalized-token → {en, ar} map. Grouped by category in comments for
 * maintainability, but intentionally a single namespace because tokens are
 * unambiguous across the categories that share them.
 */
const ENUM_LABELS: Record<string, Label> = {
  // Matter lifecycle
  INTAKE: { en: "Intake", ar: "الاستقبال" },
  IN_HEARING: { en: "In Hearing", ar: "في الجلسة" },
  NEGOTIATING: { en: "Negotiating", ar: "قيد التفاوض" },
  INTERNAL_REVIEW: { en: "Internal Review", ar: "مراجعة داخلية" },
  DRAFTING: { en: "Drafting", ar: "الصياغة" },
  RENEWAL_REVIEW: { en: "Renewal Review", ar: "مراجعة التجديد" },

  // Priority (composite + atomic)
  P0: { en: "P0", ar: "P0" },
  P1: { en: "P1", ar: "P1" },
  P2: { en: "P2", ar: "P2" },
  P3: { en: "P3", ar: "P3" },
  P1_URGENT: { en: "P1 Urgent", ar: "P1 عاجل" },
  P2_MEDIUM: { en: "P2 Medium", ar: "P2 متوسط" },
  P3_LOW: { en: "P3 Low", ar: "P3 منخفض" },

  // Alert category
  HEARING: { en: "Hearing", ar: "جلسة" },
  RENEWAL: { en: "Renewal", ar: "تجديد" },
  INVOICE: { en: "Invoice", ar: "فاتورة" },
  ALERT: { en: "Alert", ar: "تنبيه" },
  KYC: { en: "KYC", ar: "اعرف عميلك" },
  COMPLIANCE: { en: "Compliance", ar: "الامتثال" },

  // Evidence / document status
  NEW: { en: "New", ar: "جديد" },
  ANALYZING: { en: "Analyzing", ar: "قيد التحليل" },
  VALIDATED: { en: "Validated", ar: "مُتحقَّق" },
  NEEDS_REVIEW: { en: "Needs Review", ar: "بحاجة لمراجعة" },
  INTEGRITY_COMPROMISED: { en: "Integrity Compromised", ar: "النزاهة منقوصة" },
  PRIVILEGED: { en: "Privileged", ar: "محمي بالسرية" },
  REDACTED_READY: { en: "Redacted Ready", ar: "جاهز بعد التنقيح" },

  // Evidence / document type
  WITNESS_STATEMENT: { en: "Witness Statement", ar: "إفادة شاهد" },
  MEDICAL_RECORD: { en: "Medical Record", ar: "سجل طبي" },
  AUDIO: { en: "Audio", ar: "صوت" },
  VIDEO: { en: "Video", ar: "فيديو" },
  EMAIL: { en: "Email", ar: "بريد إلكتروني" },
  COURT_ORDER: { en: "Court Order", ar: "أمر محكمة" },
  EXPERT_REPORT: { en: "Expert Report", ar: "تقرير خبير" },
  CONSTRUCTION_SPEC: { en: "Construction Spec", ar: "مواصفات إنشائية" },

  // Obligation / generic status
  PENDING: { en: "Pending", ar: "قيد الانتظار" },
  COMPLETED: { en: "Completed", ar: "مكتمل" },
  COMPLETE: { en: "Complete", ar: "مكتمل" },
  OVERDUE: { en: "Overdue", ar: "متأخر" },

  // Risk / severity
  CRITICAL: { en: "Critical", ar: "حرج" },
  HIGH: { en: "High", ar: "مرتفع" },
  MEDIUM: { en: "Medium", ar: "متوسط" },
  LOW: { en: "Low", ar: "منخفض" },
  RED: { en: "Red", ar: "أحمر" },
  AMBER: { en: "Amber", ar: "كهرماني" },
  GREEN: { en: "Green", ar: "أخضر" },

  // Client status
  PROSPECT: { en: "Prospect", ar: "عميل محتمل" },
  ONBOARDING: { en: "Onboarding", ar: "قيد التسجيل" },
  ACTIVE: { en: "Active", ar: "نشِط" },
  RESTRICTED: { en: "Restricted", ar: "مقيَّد" },
  ARCHIVED: { en: "Archived", ar: "مؤرشف" },

  // KYC status
  VALID: { en: "Valid", ar: "ساري" },
  WARNING30D: { en: "Expiring in 30 days", ar: "ينتهي خلال 30 يوماً" },
  EXPIRED: { en: "Expired", ar: "منتهي" },

  // Work queue status / workflow type
  OPEN: { en: "Open", ar: "مفتوح" },
  IN_PROGRESS: { en: "In Progress", ar: "قيد التنفيذ" },
  BLOCKED: { en: "Blocked", ar: "محظور" },
  PENDING_REVIEW: { en: "Pending Review", ar: "بانتظار المراجعة" },
  ONBOARD: { en: "Onboard", ar: "تسجيل" },
  CONTRACT: { en: "Contract", ar: "عقد" },
  OBLIG: { en: "Obligation", ar: "التزام" },
  EXPORT: { en: "Export", ar: "تصدير" },

  // Invoice / billing status
  PAID: { en: "Paid", ar: "مدفوع" },
  PENDING_APPROVAL: { en: "Pending Approval", ar: "بانتظار الاعتماد" },

  // Export bundle status
  READY: { en: "Ready", ar: "جاهز" },
  QUEUED: { en: "Queued", ar: "في الطابور" },
  REVOKED: { en: "Revoked", ar: "ملغى" },

  // Communication message status
  READ: { en: "Read", ar: "مقروء" },
  UNREAD: { en: "Unread", ar: "غير مقروء" },
  SENT_CONFIRMED: { en: "Sent — Confirmed", ar: "أُرسل — مؤكَّد" },
  DELIVERY_PENDING: { en: "Delivery Pending", ar: "التسليم معلَّق" },
  BOUNCED: { en: "Bounced", ar: "مرتد" },
  FAILED: { en: "Failed", ar: "فشل" },

  // Pipeline lead stage
  INQUIRY: { en: "Inquiry", ar: "استفسار" },
  CONTACTED: { en: "Contacted", ar: "تم التواصل" },
  MEETING: { en: "Meeting", ar: "اجتماع" },
  PROPOSAL: { en: "Proposal", ar: "عرض" },
  ENGAGED: { en: "Engaged", ar: "متعاقد" },
  CLOSED: { en: "Closed", ar: "مغلق" },

  // Pipeline referral / activity type
  CLIENT: { en: "Client", ar: "عميل" },
  PARTNER: { en: "Partner", ar: "شريك" },
  PERSONAL: { en: "Personal", ar: "شخصي" },
  WEB_FORM: { en: "Web Form", ar: "نموذج إلكتروني" },
  CAMPAIGN: { en: "Campaign", ar: "حملة" },
  CALL: { en: "Call", ar: "مكالمة" },
  SYSTEM: { en: "System", ar: "النظام" },

  // Conflict status
  NONE: { en: "None", ar: "لا يوجد" },
  PASS: { en: "Pass", ar: "اجتاز" },
  REVIEW_REQUIRED: { en: "Review Required", ar: "تتطلب مراجعة" },

  // Calendar / integration status
  IDLE: { en: "Idle", ar: "خامل" },
  SYNCING: { en: "Syncing", ar: "قيد المزامنة" },
  CONNECTED: { en: "Connected", ar: "متصل" },
  NOT_CONNECTED: { en: "Not Connected", ar: "غير متصل" },
  HEALTHY: { en: "Healthy", ar: "سليم" },
  CONFIGURED: { en: "Configured", ar: "مهيأ" },
  DEGRADED: { en: "Degraded", ar: "متدهور" },
  ERROR: { en: "Error", ar: "خطأ" },

  // Role / permission
  OWNER: { en: "Owner", ar: "مالك" },
  ADMIN: { en: "Admin", ar: "مشرف" },
  MEMBER: { en: "Member", ar: "عضو" },
  PARALEGAL: { en: "Paralegal", ar: "مساعد قانوني" },
  ACCOUNTANT: { en: "Accountant", ar: "محاسب" },
  READ_ONLY: { en: "Read Only", ar: "قراءة فقط" },

  // Member / invite status
  ACCEPTED: { en: "Accepted", ar: "مقبول" },

  // Document / clause / template / KB categories
  MATTERS_FOLDERS: { en: "Matters Folders", ar: "مجلدات الملفات" },
  EVIDENCE_LOGS: { en: "Evidence Logs", ar: "سجلات الأدلة" },
  AUDITED_REVISIONS: { en: "Audited Revisions", ar: "مراجعات مدقَّقة" },
  CORPORATE_KYC: { en: "Corporate KYC", ar: "اعرف عميلك للشركات" },
  DISPUTE_RESOLUTION: { en: "Dispute Resolution", ar: "تسوية المنازعات" },
  LIABILITY_CAPS: { en: "Liability Caps", ar: "حدود المسؤولية" },
  CONFIDENTIALITY: { en: "Confidentiality", ar: "السرية" },
  INDEMNIFICATION: { en: "Indemnification", ar: "التعويض" },
  CORPORATE: { en: "Corporate", ar: "شركات" },
  EMPLOYMENT: { en: "Employment", ar: "عمل" },
  LITIGATION: { en: "Litigation", ar: "تقاضٍ" },
  COURT_PRECEDENTS: { en: "Court Precedents", ar: "سوابق قضائية" },
  MINISTERIAL_DECREES: { en: "Ministerial Decrees", ar: "قرارات وزارية" },
  FEDERAL_LAWS: { en: "Federal Laws", ar: "قوانين اتحادية" },
  FIRM_INTERNAL: { en: "Firm Internal", ar: "داخلي للمكتب" },

  // Search result type
  CASE: { en: "Case", ar: "قضية" },
  CLAUSE: { en: "Clause", ar: "بند" },
  EVIDENCE: { en: "Evidence", ar: "دليل" },

  // Subscription / firm lifecycle status (Control Tower)
  TRIALING: { en: "Trialing", ar: "تجريبي" },
  SUSPENDED: { en: "Suspended", ar: "موقوف" },
  CANCELLED: { en: "Cancelled", ar: "ملغى" },
  PAYMENT_FAILED: { en: "Payment Failed", ar: "فشل الدفع" },
  INACTIVE: { en: "Inactive", ar: "غير نشط" },
  PENDING_INVITE: { en: "Pending Invite", ar: "دعوة معلَّقة" },
  UNKNOWN: { en: "Unknown", ar: "غير معروف" },

  // Urgency
  STANDARD: { en: "Standard", ar: "عادية" },
  EXPEDITED: { en: "Expedited", ar: "معجَّلة" },
  URGENT: { en: "Urgent", ar: "عاجلة" },
};

/** Humanize an unmapped token: UPPER_SNAKE → Title Case; pass-through otherwise. */
function humanize(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  if (!/[_-]/.test(trimmed) && trimmed !== trimmed.toUpperCase()) {
    // Already a presentable mixed-case phrase (e.g. "Witness Statement").
    return trimmed;
  }
  return trimmed
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Localize a raw enum/status token for display.
 * @param value Raw token from data (may be empty / unknown).
 * @param lang  Active UI language.
 * @returns Localized human-readable label; never blank for a non-empty input.
 */
export function enumLabel(value: string | null | undefined, lang: LanguageType): string {
  if (value === null || value === undefined) return "";
  const raw = String(value);
  if (!raw.trim()) return raw;
  const entry = ENUM_LABELS[normalizeEnumKey(raw)];
  if (entry) return lang === "ar" ? entry.ar : entry.en;
  // Unmapped: humanize for both languages (Arabic gap to close over time).
  return humanize(raw);
}
